/* global BigInt */
import { ethers } from 'ethers';
import { getStoredAccessSession } from './accessControlService';
import { mempoolWebSocketService } from './mempoolWebSocketService';
import {
  TRANSACTION_PREHEAT_MODE_PREPARED,
  applyPreparedFeeOverrides,
  applyTransactionPreheatOverrides,
  isCalldataPrebuildEnabled,
  isRawPreSignEnabled,
  normalizeTransactionPreheatSettings,
  transactionPreheatService,
} from '../utils/transactionPreheat';
import {
  buildTransactionTemplateKey,
  transactionTemplateService,
} from '../utils/transactionTemplateService';
import { createTradeExecutionEnvelope } from '../utils/launchpadTradeIntent';

const ZERO_QUANTITY = '0x0';

const createTradeOperationId = () => {
  const randomPart = ethers.hexlify(ethers.randomBytes(16)).slice(2);
  return `trade-${randomPart}`;
};

const createTradeError = (result, fallbackMessage) => {
  const state = result?.state || 'unknown';
  const error = new Error(result?.error || fallbackMessage || `Transaction operation is ${state}`);
  error.operationId = result?.operationId;
  error.state = state;
  error.hash = result?.hash || '';
  error.broadcastMayHaveOccurred = state === 'unknown' || state === 'submitted' || state === 'reverted';
  error.code = state === 'unknown'
    ? 'TRANSACTION_STATUS_UNKNOWN'
    : (state === 'reverted' ? 'TRANSACTION_REVERTED' : 'TRANSACTION_FAILED');
  return error;
};

const nonceScopeFor = (provider, address) => ({
  chainId: provider?.chainId,
  rpcUrl: provider?.rpcUrl,
  address,
});

const isDefinitiveBackendNonceConflict = (error) => {
  if (!error || error.broadcastMayHaveOccurred) return false;
  const message = String(error.message || error).toLowerCase();
  return message.includes('wallet nonce coordinator floor')
    || message.includes('wallet nonce is busy')
    || message.includes('already reserved')
    || message.includes('ahead of pending nonce');
};

const toQuantity = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'string') {
    if (value.startsWith('0x')) return ethers.toQuantity(BigInt(value));
    if (/^\d+$/.test(value)) return ethers.toQuantity(BigInt(value));
    return value;
  }
  if (typeof value === 'bigint') return ethers.toQuantity(value);
  if (typeof value === 'number') return ethers.toQuantity(BigInt(value));
  return value;
};

const normalizeTxRequest = (tx = {}) => {
  const next = {
    to: tx.to || undefined,
    from: tx.from || undefined,
    data: tx.data || '0x',
    value: toQuantity(tx.value) || ZERO_QUANTITY,
    gas: toQuantity(tx.gas || tx.gasLimit),
    gasLimit: toQuantity(tx.gasLimit || tx.gas),
    gasPrice: toQuantity(tx.gasPrice),
    maxFeePerGas: toQuantity(tx.maxFeePerGas),
    maxPriorityFeePerGas: toQuantity(tx.maxPriorityFeePerGas),
    nonce: toQuantity(tx.nonce),
    chainId: toQuantity(tx.chainId),
    type: tx.type,
  };

  Object.keys(next).forEach((key) => {
    if (next[key] === undefined || next[key] === null || next[key] === '') {
      delete next[key];
    }
  });
  return next;
};

const normalizeBlockTag = (blockTag) => (
  blockTag === undefined || blockTag === null ? 'latest' : blockTag
);

const toReceiptNumber = (value, fallback = 0) => {
  if (value === undefined || value === null || value === '') return fallback;
  return Number(typeof value === 'string' ? BigInt(value) : value);
};

const toReceiptBigInt = (value) => {
  if (value === undefined || value === null || value === '') return null;
  return BigInt(value);
};

const normalizeBackendReceipt = (data, provider) => {
  if (!data || data.found === false) return null;
  if (data instanceof ethers.TransactionReceipt) return data;

  const hash = data.hash || data.transactionHash;
  const blockNumber = toReceiptNumber(data.blockNumber);
  const transactionIndex = toReceiptNumber(data.index ?? data.transactionIndex);
  const logs = Array.isArray(data.logs)
    ? data.logs.map((log) => ({
        ...log,
        blockNumber: toReceiptNumber(log.blockNumber, blockNumber),
        index: toReceiptNumber(log.index ?? log.logIndex),
        transactionIndex: toReceiptNumber(log.transactionIndex, transactionIndex),
        transactionHash: log.transactionHash || hash,
      }))
    : [];

  return new ethers.TransactionReceipt({
    ...data,
    hash,
    blockNumber,
    index: transactionIndex,
    logs,
    gasUsed: toReceiptBigInt(data.gasUsed) ?? 0n,
    cumulativeGasUsed: toReceiptBigInt(data.cumulativeGasUsed) ?? 0n,
    effectiveGasPrice: toReceiptBigInt(data.effectiveGasPrice),
    gasPrice: toReceiptBigInt(data.gasPrice),
    blobGasUsed: toReceiptBigInt(data.blobGasUsed),
    blobGasPrice: toReceiptBigInt(data.blobGasPrice),
    status: data.status === undefined || data.status === null
      ? null
      : toReceiptNumber(data.status),
    type: toReceiptNumber(data.type),
  }, provider);
};

const isTransactionHashEvent = (event) => (
  typeof event === 'string' && /^0x[0-9a-fA-F]{64}$/.test(event)
);

const normalizeWireInteger = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : value;
};

const normalizeIntegerFields = (source, fields) => {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return source;
  const normalized = { ...source };
  fields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(normalized, field)) {
      normalized[field] = normalizeWireInteger(normalized[field]);
    }
  });
  return normalized;
};

const normalizeLaunchpadSizingPayload = (sizing) => normalizeIntegerFields(
  sizing,
  ['ratioPercent', 'ratioBps']
);

const normalizeLaunchpadSidePolicyPayload = (policy) => {
  if (!policy || typeof policy !== 'object' || Array.isArray(policy)) return policy;
  return {
    ...policy,
    sizing: normalizeLaunchpadSizingPayload(policy.sizing),
  };
};

const normalizeLaunchpadTaskPayload = (payload = {}) => {
  const normalized = normalizeIntegerFields(payload, [
    'taskVersion',
    'chainId',
    'slippageBps',
    'buyPaymentDecimals',
    'sellReceiveDecimals',
    'tokenDecimals',
    'quoteDecimals',
  ]);
  const next = { ...normalized };
  if (normalized.sizing !== undefined) next.sizing = normalizeLaunchpadSizingPayload(normalized.sizing);
  if (normalized.buyPolicy !== undefined) next.buyPolicy = normalizeLaunchpadSidePolicyPayload(normalized.buyPolicy);
  if (normalized.sellPolicy !== undefined) next.sellPolicy = normalizeLaunchpadSidePolicyPayload(normalized.sellPolicy);
  if (normalized.limits !== undefined) {
    next.limits = normalizeIntegerFields(normalized.limits, ['maxSuccessfulTransactions']);
  }
  if (normalized.proxy !== undefined) {
    next.proxy = normalizeIntegerFields(normalized.proxy, [
      'buyMaxBurnPercent',
      'sellMaxBurnPercent',
      'maxBurnBps',
      'maxTaxBps',
    ]);
  }
  if (Array.isArray(normalized.paymentAssets)) {
    next.paymentAssets = normalized.paymentAssets.map((asset) => normalizeIntegerFields(asset, ['decimals']));
  }
  if (Array.isArray(normalized.receiveAssets)) {
    next.receiveAssets = normalized.receiveAssets.map((asset) => normalizeIntegerFields(asset, ['decimals']));
  }
  if (Array.isArray(normalized.externalRoutes)) {
    next.externalRoutes = normalized.externalRoutes.map((route) => ({
          ...normalizeIntegerFields(route, ['fee']),
          fees: Array.isArray(route?.fees) ? route.fees.map(normalizeWireInteger) : route?.fees,
          tickSpacings: Array.isArray(route?.tickSpacings)
            ? route.tickSpacings.map(normalizeWireInteger)
            : route?.tickSpacings,
        }));
  }
  if (normalized.gasPolicy && Object.prototype.hasOwnProperty.call(normalized.gasPolicy, 'multiplier')) {
    const multiplier = Number(normalized.gasPolicy.multiplier);
    next.gasPolicy = {
      ...normalized.gasPolicy,
      multiplier: Number.isFinite(multiplier) ? multiplier : normalized.gasPolicy.multiplier,
    };
  }
  return next;
};

export function buildRpcConnectPayload({
  chainKey,
  chainId,
  chainName,
  rpcUrl,
  rpcUrls,
  blockTimeMs,
}) {
  const normalizedRpcUrls = Array.from(new Set(
    (Array.isArray(rpcUrls) ? rpcUrls : [rpcUrl])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  ));

  return {
    chainKey,
    chainId: Number(chainId || 0),
    chainName,
    rpcUrl: normalizedRpcUrls[0] || '',
    rpcUrls: normalizedRpcUrls,
    accessToken: getStoredAccessSession()?.token || '',
    blockTimeMs: Number(blockTimeMs || 0),
  };
}

class BackendTransactionResponse {
  constructor({ hash, provider, raw = {} }) {
    this.hash = hash;
    this.provider = provider;
    Object.assign(this, raw);
  }

  async wait(confirms = 1, timeoutMs) {
    return this.provider.waitForTransaction(this.hash, confirms, timeoutMs);
  }
}

export function transactionFromPendingEvent(event, provider) {
  if (!event || typeof event === 'string' || !event.transaction) return null;
  const transaction = event.transaction;
  const hash = event.hash || transaction.hash || '';
  if (!hash) return null;

  return new BackendTransactionResponse({
    hash,
    provider,
    raw: {
      ...transaction,
      hash,
      data: transaction.data || transaction.input || '0x',
      rawTx: event.rawTx || transaction.rawTx || transaction.raw || '',
      sourceEndpoint: event.sourceEndpoint || transaction.sourceEndpoint || '',
      receivedAtUnixNano: event.receivedAtUnixNano || transaction.receivedAtUnixNano,
    },
  });
}

export class BackendRpcProvider {
  constructor({ chainKey, chainId, chainName, rpcUrl, blockTimeMs }) {
    this.chainKey = chainKey || '';
    this.chainId = Number(chainId || 0) || null;
    this.chainName = chainName || chainKey || 'unknown';
    this.rpcUrl = rpcUrl || '';
    this.blockTimeMs = blockTimeMs || 1000;
    this.pollingInterval = this.blockTimeMs;
    this._listeners = new Map();
    this._transactionReceiptPollers = new Map();
    this.provider = this;
    this._pendingForwarder = null;
    this._pendingSubscriptionActive = false;
    this._pendingSubscriptionOptions = { routerAddresses: [] };
    this._pendingSubscriptionGeneration = 0;
    this._pendingConnectionListenersAttached = false;
  }

  async getNetwork() {
    return ethers.Network.from({
      chainId: BigInt(this.chainId || 0),
      name: this.chainName || 'unknown',
    });
  }

  async getBlockNumber() {
    const data = await backendRpcService.chainRead('block_number');
    return Number(data.blockNumber ?? data.result ?? 0);
  }

  async getFeeData() {
    const data = await backendRpcService.chainRead('fee_data');
    return {
      gasPrice: data.gasPrice ? BigInt(data.gasPrice) : null,
      maxFeePerGas: data.maxFeePerGas ? BigInt(data.maxFeePerGas) : null,
      maxPriorityFeePerGas: data.maxPriorityFeePerGas ? BigInt(data.maxPriorityFeePerGas) : null,
    };
  }

  async getBalance(address, blockTag = 'latest') {
    const data = await backendRpcService.chainRead('native_balance', { address, blockTag: normalizeBlockTag(blockTag) });
    return BigInt(data.balance || data.result || 0);
  }

  async getCode(address, blockTag = 'latest') {
    const data = await backendRpcService.chainRead('get_code', { address, blockTag: normalizeBlockTag(blockTag) });
    return data.code || data.result || '0x';
  }

  async getTransactionCount(address, blockTag = 'pending') {
    const data = await backendRpcService.chainRead('transaction_count', { address, blockTag: normalizeBlockTag(blockTag) });
    return Number(data.nonce ?? data.result ?? 0);
  }

  async call(transaction, blockTag = 'latest') {
    const data = await backendRpcService.chainRead('contract_call', {
      transaction: normalizeTxRequest(transaction),
      blockTag: normalizeBlockTag(blockTag),
    });
    return data.result || '0x';
  }

  async estimateGas(transaction) {
    const data = await backendRpcService.chainRead('estimate_gas', {
      transaction: normalizeTxRequest(transaction),
    });
    return BigInt(data.gas || data.result || 0);
  }

  async getTransaction(hash) {
    const data = await backendRpcService.chainRead('transaction', { hash });
    if (!data || data.found === false) return null;
    return new BackendTransactionResponse({ hash, provider: this, raw: data });
  }

  async getTransactionReceipt(hash) {
    const data = await backendRpcService.chainRead('transaction_receipt', { hash });
    return normalizeBackendReceipt(data, this);
  }

  async waitForTransaction(hash, confirms = 1, timeoutMs = 120000) {
    const startedAt = Date.now();
    const requiredConfirmations = Math.max(1, Number(confirms || 1));
    while (Date.now() - startedAt < timeoutMs) {
      const receipt = await this.getTransactionReceipt(hash);
      if (receipt) {
        const status = receipt.status;
        const reverted = status === false
          || status === 0
          || status === 0n
          || (typeof status === 'string' && BigInt(status) === 0n);
        if (reverted) {
          const error = new Error(`Transaction ${hash} reverted`);
          error.code = 'TRANSACTION_REVERTED';
          error.receipt = receipt;
          throw error;
        }
        if (requiredConfirmations === 1) return receipt;

        const receiptBlock = Number(receipt.blockNumber);
        if (Number.isFinite(receiptBlock) && receiptBlock >= 0) {
          const currentBlock = await this.getBlockNumber();
          if (currentBlock >= receiptBlock + requiredConfirmations - 1) return receipt;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, Math.max(500, Math.min(this.blockTimeMs, 2000))));
    }
    const error = new Error(`Timed out waiting for transaction ${hash}`);
    error.code = 'TRANSACTION_CONFIRMATION_TIMEOUT';
    error.hash = hash;
    throw error;
  }

  async resolveName(name) {
    return ethers.isAddress(name) ? name : null;
  }

  async broadcastTransaction() {
    throw new Error('Browser raw transaction broadcast is disabled; use backend trade_execute');
  }

  on(event, callback) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(callback);
    if (event === 'pending') {
      this.startPendingSubscription();
    } else if (isTransactionHashEvent(event)) {
      this.startTransactionReceiptPolling(event);
    }
    return this;
  }

  off(event, callback) {
    this._listeners.get(event)?.delete(callback);
    if (!this._listeners.get(event) || this._listeners.get(event).size === 0) {
      this._listeners.delete(event);
      if (event === 'pending') {
        this.stopPendingSubscription();
      } else if (isTransactionHashEvent(event)) {
        this.stopTransactionReceiptPolling(event);
      }
    }
    return this;
  }

  detachLocalListener(event, callback) {
    const listeners = this._listeners.get(event);
    listeners?.delete(callback);
    if (!listeners || listeners.size === 0) {
      this._listeners.delete(event);
      if (event === 'pending' && this._pendingForwarder) {
        mempoolWebSocketService.off('pending_tx', this._pendingForwarder);
        this._pendingForwarder = null;
      } else if (isTransactionHashEvent(event)) {
        this.stopTransactionReceiptPolling(event);
      }
    }
    return this;
  }

  once(event, callback) {
    const onceCallback = (value) => {
      this.off(event, onceCallback);
      callback(value);
    };
    return this.on(event, onceCallback);
  }

  emit(event, value) {
    this._listeners.get(event)?.forEach((callback) => callback(value));
  }

  removeAllListeners(event) {
    if (event !== undefined) {
      this._listeners.delete(event);
      if (event === 'pending') this.stopPendingSubscription();
      if (isTransactionHashEvent(event)) this.stopTransactionReceiptPolling(event);
      return;
    }
    this._listeners.clear();
    for (const hash of this._transactionReceiptPollers.keys()) {
      this.stopTransactionReceiptPolling(hash);
    }
  }

  startTransactionReceiptPolling(hash) {
    if (this._transactionReceiptPollers.has(hash)) return;
    const state = { stopped: false, timer: null };
    const poll = async () => {
      if (state.stopped) return;
      const listeners = this._listeners.get(hash);
      if (!listeners || listeners.size === 0) {
        this.stopTransactionReceiptPolling(hash);
        return;
      }

      try {
        const receipt = await this.getTransactionReceipt(hash);
        if (receipt) {
          this.stopTransactionReceiptPolling(hash);
          this.emit(hash, receipt);
          return;
        }
      } catch (_) {
        // A transient read failure must not lose an already-broadcast transaction.
      }

      if (!state.stopped) {
        const intervalMs = Math.max(250, Math.min(Number(this.blockTimeMs || 1000), 2000));
        state.timer = setTimeout(poll, intervalMs);
      }
    };

    this._transactionReceiptPollers.set(hash, state);
    Promise.resolve().then(poll);
  }

  stopTransactionReceiptPolling(hash) {
    const state = this._transactionReceiptPollers.get(hash);
    if (!state) return;
    state.stopped = true;
    if (state.timer) clearTimeout(state.timer);
    this._transactionReceiptPollers.delete(hash);
  }

  destroy() {
    this.stopPendingSubscription();
    this.removeAllListeners();
  }

  attachPendingConnectionListeners() {
    if (this._pendingConnectionListenersAttached) return;
    this._pendingConnectionListenersAttached = true;
    mempoolWebSocketService.on('connected', this.handleBackendConnected);
    mempoolWebSocketService.on('disconnected', this.handleBackendDisconnected);
  }

  detachPendingConnectionListeners() {
    if (!this._pendingConnectionListenersAttached) return;
    this._pendingConnectionListenersAttached = false;
    mempoolWebSocketService.off('connected', this.handleBackendConnected);
    mempoolWebSocketService.off('disconnected', this.handleBackendDisconnected);
  }

  handleBackendConnected() {
    // The Go session owns active pending subscriptions across frontend reconnects.
  }

  handleBackendDisconnected() {
    // Keep the local active flag so reconnect never creates a duplicate subscription.
  }

  configurePendingSubscription({ routerAddresses = [] } = {}) {
    this._pendingSubscriptionOptions = {
      routerAddresses: [...new Set(
        routerAddresses
          .filter((address) => ethers.isAddress(address))
          .map((address) => ethers.getAddress(address))
      )],
    };
    return this;
  }

  startPendingSubscription() {
    if (!this._pendingForwarder) {
      this._pendingForwarder = (data) => {
        const hash = typeof data === 'string' ? data : data?.hash;
        if (!hash) return;
        this.emit('pending', data?.transaction || data?.rawTx ? data : hash);
      };
      mempoolWebSocketService.on('pending_tx', this._pendingForwarder);
    }
    if (this._pendingSubscriptionActive) return Promise.resolve({ subscribed: true });
    const generation = ++this._pendingSubscriptionGeneration;
    this._pendingSubscriptionActive = true;
    return backendRpcService.startPendingSubscription(this._pendingSubscriptionOptions).catch((error) => {
      if (generation === this._pendingSubscriptionGeneration) {
        this._pendingSubscriptionActive = false;
      }
      this.emit('error', error);
      return { subscribed: false, error };
    });
  }

  stopPendingSubscription() {
    if (this._pendingForwarder) {
      mempoolWebSocketService.off('pending_tx', this._pendingForwarder);
      this._pendingForwarder = null;
    }
    this.detachPendingConnectionListeners();
    if (this._pendingSubscriptionActive) {
      this._pendingSubscriptionGeneration += 1;
      this._pendingSubscriptionActive = false;
      backendRpcService.stopPendingSubscription().catch(() => {});
    }
  }
}

class BackendWallet {
  constructor(privateKey, provider) {
    this.privateKey = privateKey;
    this.provider = provider;
    this.localWallet = new ethers.Wallet(privateKey);
    this.address = this.localWallet.address;
  }

  async getAddress() {
    return this.address;
  }

  connect(provider) {
    return new BackendWallet(this.privateKey, provider);
  }

  async getNonce(blockTag = 'pending') {
    return this.provider.getTransactionCount(this.address, blockTag);
  }

  async call(transaction) {
    return this.provider.call({ from: this.address, ...transaction });
  }

  async estimateGas(transaction) {
    return this.provider.estimateGas({ from: this.address, ...transaction });
  }

  async resolveName(name) {
    return this.provider.resolveName(name);
  }

  async sendTransaction(transaction) {
    const compatibilityKind = transaction?.compatibilityKind || '';
    const settings = normalizeTransactionPreheatSettings(transactionPreheatService.getSettings());
    let txRequest = applyPreparedFeeOverrides(
      applyTransactionPreheatOverrides(transaction, settings),
      settings,
      transactionPreheatService.getFeeData()
    );
    if (isCalldataPrebuildEnabled(settings)) {
      const calldataKey = buildTransactionTemplateKey({
        chainId: this.provider?.chainId,
        rpcUrl: this.provider?.rpcUrl,
        from: this.address,
        transaction: txRequest,
      });
      const calldataTemplate = transactionTemplateService.peekCalldataTemplate(calldataKey);
      if (calldataTemplate?.txRequest) {
        txRequest = { ...calldataTemplate.txRequest };
      }
    }

    if (isRawPreSignEnabled(settings)) {
      const nonceScope = nonceScopeFor(this.provider, this.address);
      const templateKey = buildTransactionTemplateKey({
        chainId: this.provider?.chainId,
        rpcUrl: this.provider?.rpcUrl,
        from: this.address,
        transaction: txRequest,
      });
      const template = transactionTemplateService.consumeRawTemplate(templateKey);
      let rawTx = template?.rawTx;
      let reservedNonce = null;
      if (template?.nonce !== undefined && template?.nonce !== null) {
        const templateNonce = BigInt(template.nonce);
        if (transactionPreheatService.reserveSpecificNonce(nonceScope, templateNonce)) {
          reservedNonce = templateNonce;
        } else {
          rawTx = null;
        }
      }
      if (!rawTx) {
        if (txRequest.nonce === undefined || txRequest.nonce === null || txRequest.nonce === '') {
          reservedNonce = transactionPreheatService.reserveNonce(nonceScope);
          if (reservedNonce !== null) {
            txRequest.nonce = reservedNonce;
          }
        }
        try {
          rawTx = await this.signTransaction(txRequest);
        } catch (error) {
          if (reservedNonce !== null) transactionPreheatService.releaseNonce(nonceScope, reservedNonce);
          throw error;
        }
      }
      let result;
      try {
        result = await backendRpcService.executeTransaction({
          mode: 'raw_transaction',
          compatibilityKind,
          chainKey: this.provider?.chainKey,
          chainId: this.provider?.chainId,
          rpcUrl: this.provider?.rpcUrl,
          rawTx,
          ...(compatibilityKind ? {
            transaction: {
              from: this.address,
              ...normalizeTxRequest(txRequest),
            },
          } : {}),
        });
      } catch (error) {
        if (isDefinitiveBackendNonceConflict(error)) {
          if (reservedNonce !== null) transactionPreheatService.releaseNonce(nonceScope, reservedNonce);
          const rebuiltTransaction = normalizeTxRequest({
            from: this.address,
            ...txRequest,
          });
          delete rebuiltTransaction.nonce;
          result = await backendRpcService.executeTransaction({
            compatibilityKind,
            chainKey: this.provider?.chainKey,
            chainId: this.provider?.chainId,
            rpcUrl: this.provider?.rpcUrl,
            privateKey: this.privateKey,
            transaction: rebuiltTransaction,
          });
          return new BackendTransactionResponse({ hash: result.hash, provider: this.provider, raw: result });
        }
        if (reservedNonce !== null) {
          if (error?.broadcastMayHaveOccurred) transactionPreheatService.invalidateNonce(nonceScope, reservedNonce);
          else transactionPreheatService.releaseNonce(nonceScope, reservedNonce);
        }
        throw error;
      }
      if (reservedNonce !== null) transactionPreheatService.commitNonce(nonceScope, reservedNonce);
      return new BackendTransactionResponse({ hash: result.hash, provider: this.provider, raw: result });
    }

    const nonceScope = nonceScopeFor(this.provider, this.address);
    let reservedNonce = null;
    if (
      settings.preheatMode === TRANSACTION_PREHEAT_MODE_PREPARED
      && (txRequest.nonce === undefined || txRequest.nonce === null || txRequest.nonce === '')
    ) {
      reservedNonce = transactionPreheatService.reserveNonce(nonceScope);
      if (reservedNonce !== null) {
        txRequest.nonce = reservedNonce;
      }
    }

    let result;
    try {
      result = await backendRpcService.executeTransaction({
        compatibilityKind,
        chainKey: this.provider?.chainKey,
        chainId: this.provider?.chainId,
        rpcUrl: this.provider?.rpcUrl,
        privateKey: this.privateKey,
        transaction: {
          from: this.address,
          ...normalizeTxRequest(txRequest),
        },
      });
    } catch (error) {
      if (reservedNonce !== null) {
        if (error?.broadcastMayHaveOccurred) transactionPreheatService.invalidateNonce(nonceScope, reservedNonce);
        else transactionPreheatService.releaseNonce(nonceScope, reservedNonce);
      }
      throw error;
    }
    if (reservedNonce !== null) transactionPreheatService.commitNonce(nonceScope, reservedNonce);
    return new BackendTransactionResponse({ hash: result.hash, provider: this.provider, raw: result });
  }

  async signTransaction(transaction, { reservePreparedNonce = true } = {}) {
    const settings = normalizeTransactionPreheatSettings(transactionPreheatService.getSettings());
    const tx = normalizeTxRequest(applyPreparedFeeOverrides(
      applyTransactionPreheatOverrides(transaction, settings),
      settings,
      transactionPreheatService.getFeeData()
    ));
    if (!tx.chainId && this.provider?.chainId) {
      tx.chainId = toQuantity(this.provider.chainId);
    }
    let reservedNonce = null;
    const nonceScope = nonceScopeFor(this.provider, this.address);
    try {
      if (tx.nonce === undefined || tx.nonce === null || tx.nonce === '') {
        reservedNonce = reservePreparedNonce && settings.preheatMode === TRANSACTION_PREHEAT_MODE_PREPARED
          ? transactionPreheatService.reserveNonce(nonceScope)
          : null;
        tx.nonce = toQuantity(reservedNonce !== null ? reservedNonce : await this.getNonce('pending'));
      }
      if (!tx.gasLimit && !tx.gas) {
        tx.gasLimit = toQuantity(await this.estimateGas(tx));
      }
      if (!tx.gasPrice && !tx.maxFeePerGas && !tx.maxPriorityFeePerGas) {
        const feeData = await this.provider.getFeeData();
        if (feeData?.gasPrice) {
          tx.gasPrice = toQuantity(feeData.gasPrice);
        } else if (feeData?.maxFeePerGas || feeData?.maxPriorityFeePerGas) {
          tx.maxFeePerGas = toQuantity(feeData.maxFeePerGas || 0n);
          tx.maxPriorityFeePerGas = toQuantity(feeData.maxPriorityFeePerGas || 0n);
        }
      }
      delete tx.from;
      delete tx.gas;
      return await this.localWallet.signTransaction(tx);
    } catch (error) {
      if (reservedNonce !== null) transactionPreheatService.releaseNonce(nonceScope, reservedNonce);
      throw error;
    }
  }

  async prepareTransaction(transaction, { ttlMs = 5000, source = 'backend-wallet' } = {}) {
    const settings = normalizeTransactionPreheatSettings(transactionPreheatService.getSettings());
    const txRequest = applyPreparedFeeOverrides(
      applyTransactionPreheatOverrides(transaction, settings),
      settings,
      transactionPreheatService.getFeeData()
    );
    const key = buildTransactionTemplateKey({
      chainId: this.provider?.chainId,
      rpcUrl: this.provider?.rpcUrl,
      from: this.address,
      transaction: txRequest,
      source,
    });
    transactionTemplateService.storeCalldataTemplate(key, {
      txRequest,
      expiresAt: Date.now() + Math.max(250, Number(ttlMs || 5000)),
      source,
    });
    if (!isRawPreSignEnabled(settings)) {
      return transactionTemplateService.peekCalldataTemplate(key);
    }
    const existing = transactionTemplateService.peekRawTemplate(key);
    if (existing) return existing;
    if (txRequest.nonce === undefined || txRequest.nonce === null || txRequest.nonce === '') {
      txRequest.nonce = await this.getNonce('pending');
    }
    const rawTx = await this.signTransaction(txRequest, { reservePreparedNonce: false });
    const parsed = ethers.Transaction.from(rawTx);
    return transactionTemplateService.storeRawTemplate(key, {
      rawTx,
      hash: parsed.hash,
      nonce: parsed.nonce,
      expiresAt: Date.now() + Math.max(250, Number(ttlMs || 5000)),
      source,
    });
  }
}

export const createBackendWallet = (privateKey, provider) => new BackendWallet(privateKey, provider);

const isBackendRpcNotConnectedError = (error) => (
  /backend\s+rpc\s+is\s+not\s+connected/i.test(error?.message || String(error || ''))
);

export class BackendPersistentSubscription {
  constructor(socketService, {
    subscribeType,
    unsubscribeType,
    timeoutMs = 15000,
    unsubscribeTimeoutMs = 10000,
    beforeSend = null,
  }) {
    this.socketService = socketService;
    this.subscribeType = subscribeType;
    this.unsubscribeType = unsubscribeType;
    this.timeoutMs = timeoutMs;
    this.unsubscribeTimeoutMs = unsubscribeTimeoutMs;
    this.beforeSend = typeof beforeSend === 'function' ? beforeSend : null;
    this.activePayload = null;
    this.inFlight = null;
  }

  sendActive() {
    if (!this.activePayload) return Promise.resolve({ subscribed: false });
    if (this.inFlight) return this.inFlight;
    const payload = this.activePayload;
    const send = () => {
      if (this.activePayload !== payload) return { subscribed: false };
      return this.socketService.request(
        this.subscribeType,
        payload,
        { timeoutMs: this.timeoutMs }
      );
    };
    const request = this.beforeSend
      ? Promise.resolve().then(() => this.beforeSend()).then(send)
      : send();
    this.inFlight = request;
    return request.finally(() => {
      if (this.inFlight === request) this.inFlight = null;
    });
  }

  subscribe(payload) {
    this.activePayload = { ...payload };
    return this.sendActive();
  }

  async unsubscribe() {
    this.activePayload = null;
    const pending = this.inFlight;
    if (pending) {
      try {
        await pending;
      } catch (_) {}
    }
    this.inFlight = null;
    return this.socketService.request(
      this.unsubscribeType,
      {},
      { timeoutMs: this.unsubscribeTimeoutMs }
    );
  }
}

export class BackendRpcSessionManager {
  constructor(socketService) {
    this.socketService = socketService;
    this.settings = null;
    this.ready = false;
    this.restorePromise = null;
    this.generation = 0;
    this.handleConnected = this.handleConnected.bind(this);
    this.handleDisconnected = this.handleDisconnected.bind(this);
    this.socketService.on?.('connected', this.handleConnected);
    this.socketService.on?.('disconnected', this.handleDisconnected);
  }

  handleConnected() {
    if (!this.settings || this.ready || this.restorePromise) return;
    this.restore().catch((error) => {
      console.warn('Backend RPC session restore failed:', error?.message || error);
    });
  }

  handleDisconnected() {
    if (this.settings) this.ready = false;
  }

  runConnect(settings, generation = this.generation) {
    if (this.restorePromise) return this.restorePromise;
    const promise = this.socketService.request(
      'rpc_connect',
      buildRpcConnectPayload(settings),
      { timeoutMs: 30000 }
    );
    this.restorePromise = promise;

    return promise
      .then((result) => {
        if (generation === this.generation && this.settings) {
          this.ready = true;
        }
        return result;
      })
      .finally(() => {
        if (this.restorePromise === promise) this.restorePromise = null;
      });
  }

  connect(settings) {
    this.generation += 1;
    this.settings = { ...settings };
    this.ready = false;
    return this.runConnect(this.settings, this.generation);
  }

  restore() {
    if (!this.settings) {
      return Promise.reject(new Error('No backend RPC session is configured'));
    }
    if (this.ready) return Promise.resolve({ connected: true, restored: false });
    return this.runConnect(this.settings, this.generation);
  }

  async disconnect() {
    const pendingRestore = this.restorePromise;
    this.generation += 1;
    this.settings = null;
    this.ready = false;
    if (pendingRestore) {
      try {
        await pendingRestore;
      } catch (_) {}
    }
    this.restorePromise = null;
    return this.socketService.request('rpc_disconnect', {}, { timeoutMs: 10000 });
  }

  async ensureReady() {
    if (!this.settings) return;
    await this.socketService.waitForConnection?.(10000);
    if (this.ready) return;
    await (this.restorePromise || this.restore());
  }

  async requestWithRpcSession(type, data = {}, options = {}) {
    await this.ensureReady();
    try {
      return await this.socketService.request(type, data, options);
    } catch (error) {
      if (!this.settings || !isBackendRpcNotConnectedError(error)) throw error;
      this.ready = false;
      await this.restore();
      return this.socketService.request(type, data, options);
    }
  }
}

export class BackendFrontrunHotpathManager {
  constructor(socketService, rpcSessionManager) {
    this.socketService = socketService;
    this.rpcSessionManager = rpcSessionManager;
    this.activePayload = null;
    this.inFlight = null;
    this.ready = false;
  }

  sendActive() {
    if (!this.activePayload) return Promise.resolve({ ready: false });
    if (this.inFlight) {
      const pending = this.inFlight;
      return pending
        .catch(() => undefined)
        .then(() => {
          if (!this.activePayload) return { ready: false };
          if (this.ready) return { ready: true };
          return this.sendActive();
        });
    }
    const payload = this.activePayload;
    const request = this.rpcSessionManager.requestWithRpcSession(
      'blockrazor_frontrun_prepare',
      payload,
      { timeoutMs: 30000 }
    )
      .then((result) => {
        if (this.activePayload === payload) {
          this.ready = Boolean(result?.ready);
          if (!this.ready) throw new Error('BlockRazor Frontrun hotpath is not ready');
        }
        return result;
      })
      .finally(() => {
        if (this.inFlight === request) this.inFlight = null;
      });
    this.inFlight = request;
    return request;
  }

  prepare(payload) {
    this.activePayload = { ...payload };
    this.ready = false;
    return this.sendActive();
  }

  async stop() {
    this.activePayload = null;
    this.ready = false;
    const pending = this.inFlight;
    if (pending) {
      try {
        await pending;
      } catch (_) {}
    }
    this.inFlight = null;
    return this.rpcSessionManager.requestWithRpcSession(
      'blockrazor_frontrun_stop',
      {},
      { timeoutMs: 10000 }
    );
  }
  ensureReady() {
    if (!this.activePayload || this.ready) return Promise.resolve();
    return this.inFlight || this.sendActive();
  }
}

const backendRpcSessionManager = new BackendRpcSessionManager(mempoolWebSocketService);
const blockrazorFrontrunHotpath = new BackendFrontrunHotpathManager(
  mempoolWebSocketService,
  backendRpcSessionManager
);
const blockrazorCopySubscription = new BackendPersistentSubscription(mempoolWebSocketService, {
  subscribeType: 'blockrazor_copy_subscribe',
  unsubscribeType: 'blockrazor_copy_unsubscribe',
  timeoutMs: 15000,
  unsubscribeTimeoutMs: 10000,
});
const blockrazorPublicSubscription = new BackendPersistentSubscription(mempoolWebSocketService, {
  subscribeType: 'blockrazor_public_subscribe',
  unsubscribeType: 'blockrazor_public_unsubscribe',
  timeoutMs: 15000,
  unsubscribeTimeoutMs: 10000,
  beforeSend: () => blockrazorFrontrunHotpath.ensureReady(),
});

const blockrazorUnsentError = (message, code = 'BLOCKRAZOR_PAYLOAD_INVALID') => {
  const error = new Error(message);
  error.requestSent = false;
  error.code = code;
  error.blockrazorDefinitive = true;
  return error;
};

const submitBlockrazorFollowBundle = async (payload = {}) => {
  const operationId = String(payload.operationId || '').trim();
  const chainKey = String(payload.chainKey || '').trim().toLowerCase();
  const chainId = Number(payload.chainId || 0);
  const rawTargetBundleHash = String(payload.targetBundleHash || '');
  const targetBundleHash = rawTargetBundleHash.trim();
  const rawTx = String(payload.rawTx || '');
  const rawTxs = Array.isArray(payload.rawTxs) ? payload.rawTxs : [];
  const hasRawTransaction = [rawTx, ...rawTxs]
    .some((value) => typeof value === 'string' && value.startsWith('0x') && value.length > 2);
  const maxBlockNumber = Number(payload.maxBlockNumber || 0);

  if (!operationId) throw blockrazorUnsentError('operationId is required');
  if (chainKey !== 'bsc' || chainId !== 56) {
    throw blockrazorUnsentError('BlockRazor follow bundles are only supported on BSC');
  }
  if (!targetBundleHash) throw blockrazorUnsentError('targetBundleHash is required');
  if (rawTargetBundleHash !== targetBundleHash || !/^0x[0-9a-f]{64}$/i.test(targetBundleHash)) {
    throw blockrazorUnsentError('targetBundleHash must be a canonical 32-byte hash');
  }
  if (!hasRawTransaction) throw blockrazorUnsentError('rawTxs is required');
  if (!Number.isSafeInteger(maxBlockNumber) || maxBlockNumber <= 0) {
    throw blockrazorUnsentError('maxBlockNumber is required');
  }

  return mempoolWebSocketService.request(
    'blockrazor_follow_bundle',
    {
      chainKey,
      chainId,
      operationId,
      authToken: payload.authToken || '',
      builderEndpoint: payload.builderEndpoint || '',
      builderEndpoints: Array.isArray(payload.builderEndpoints) ? payload.builderEndpoints : [],
      sourceEndpoint: payload.sourceEndpoint || '',
      targetBundleHash,
      rawTx,
      rawTxs,
      maxBlockNumber,
      refundAddress: payload.refundAddress || '',
      hintCalldata: !!payload.hintCalldata,
    },
    { timeoutMs: 120000 }
  );
};

export const backendRpcService = {
  getPersistentTaskStatus: () => mempoolWebSocketService.getPersistentTaskStatus?.()
    || { tasks: [], recent_events: [], session_id: '' },
  listPersistentTasks: () => mempoolWebSocketService.request(
    'task_list',
    {},
    { timeoutMs: 10000 }
  ),
  stopPersistentTask: (taskId) => mempoolWebSocketService.request(
    'task_stop',
    { task_id: String(taskId || '') },
    { timeoutMs: 10000 }
  ),
  onPersistentTaskStatus: (callback) => {
    mempoolWebSocketService.on('task_status', callback);
    return () => mempoolWebSocketService.off('task_status', callback);
  },
  startLaunchpadCopyTask: (payload) => mempoolWebSocketService.request(
    'launchpad_copy_start',
    normalizeLaunchpadTaskPayload(payload),
    { timeoutMs: 30000 }
  ),
  startLaunchpadTask: (payload) => mempoolWebSocketService.request(
    'launchpad_copy_start',
    normalizeLaunchpadTaskPayload(payload),
    { timeoutMs: 30000 }
  ),
  onLaunchpadCopyEvent: (callback) => {
    mempoolWebSocketService.on('launchpad_copy_event', callback);
    return () => mempoolWebSocketService.off('launchpad_copy_event', callback);
  },
  onLaunchpadTaskEvent: (callback) => {
    mempoolWebSocketService.on('launchpad_task_event', callback);
    return () => mempoolWebSocketService.off('launchpad_task_event', callback);
  },
  connectRpc: (settings) => backendRpcSessionManager.connect(settings),
  disconnectRpc: () => backendRpcSessionManager.disconnect(),
  chainRead: (operation, params = {}) => backendRpcSessionManager.requestWithRpcSession(
    'chain_read',
    { operation, params },
    { timeoutMs: 30000 }
  ),
  executeTransaction: async (payload) => {
    const operationId = String(payload.operationId || createTradeOperationId());
    const mode = payload.mode || 'transaction_request';
    const sessionSettings = backendRpcSessionManager.settings || {};
    const chainKey = payload.chainKey || sessionSettings.chainKey || '';
    const chainId = Number(payload.chainId || sessionSettings.chainId || 0);
    const chainName = payload.chainName || sessionSettings.chainName || '';
    const transaction = normalizeTxRequest(
      payload.transaction || payload.executionPlan?.execution?.transaction || {}
    );
    const hasExpandedEnvelope = Boolean(payload.tradeIntent || payload.resolution || payload.executionPlan);
    const isOrdinaryDexCompatibility = payload.compatibilityKind === 'ordinary_dex';
    const envelope = hasExpandedEnvelope || isOrdinaryDexCompatibility
      ? createTradeExecutionEnvelope({
        tradeIntent: payload.tradeIntent,
        resolution: payload.resolution,
        executionPlan: payload.executionPlan,
        chainKey,
        chainId,
        chainName,
        transaction,
        walletAddress: transaction.from || payload.walletAddress || '',
      })
      : null;
    let result;
    try {
      result = await backendRpcSessionManager.requestWithRpcSession(
        'trade_execute',
        {
          operationId,
          mode,
          chainKey,
          chainId,
          rpcUrl: payload.rpcUrl || '',
          privateKey: payload.privateKey,
          rawTx: payload.rawTx,
          transaction,
          tradeIntent: envelope?.intent,
          resolution: envelope?.resolution,
          executionPlan: envelope?.executionPlan,
        },
        { timeoutMs: 120000 }
      );
    } catch (originalError) {
      if (originalError?.requestSent) {
        throw createTradeError({
          operationId,
          state: 'unknown',
          error: originalError.message,
        });
      }
      throw originalError;
    }
    const normalized = { operationId, state: 'submitted', ...result };
    if (normalized.state === 'failed' || normalized.state === 'reverted' || normalized.state === 'unknown') {
      throw createTradeError(normalized);
    }
    return normalized;
  },
  getTradeStatus: (operationId) => backendRpcSessionManager.requestWithRpcSession(
    'trade_status',
    { operationId: String(operationId || '') },
    { timeoutMs: 30000 }
  ),
  submitBlockrazorBundle: (payload) => {
    const requestPayload = {
      chainKey: payload.chainKey || '',
      chainId: Number(payload.chainId || 0),
      authToken: payload.authToken || '',
      builderEndpoint: payload.builderEndpoint || '',
      builderEndpoints: Array.isArray(payload.builderEndpoints) ? payload.builderEndpoints : [],
      operationId: payload.operationId || '',
      rawTxs: Array.isArray(payload.rawTxs) ? payload.rawTxs : [],
      bundleOrder: payload.bundleOrder || 'frontrun',
      maxBlockNumber: Number(payload.maxBlockNumber || 0),
      strictPosition: !!payload.strictPosition,
      noMerge: !!payload.noMerge,
      simulateBeforeSend: payload.simulateBeforeSend === true,
      diagnoseOnReject: payload.diagnoseOnReject === true,
    };
    if (Number(payload.targetReceivedAtUnixNano || 0) > 0) {
      requestPayload.targetReceivedAtUnixNano = Number(payload.targetReceivedAtUnixNano);
    }
    return backendRpcSessionManager.requestWithRpcSession(
      'blockrazor_bundle',
      requestPayload,
      { timeoutMs: 120000 }
    );
  },
  getBlockrazorAuthStatus: ({ chainKey = '', chainId = 0 } = {}) => mempoolWebSocketService.request(
    'blockrazor_auth_status',
    { chainKey: String(chainKey || ''), chainId: Number(chainId || 0) },
    { timeoutMs: 10000 }
  ),
  getBlockrazorBundleStatus: (operationId) => mempoolWebSocketService.request(
    'blockrazor_bundle_status',
    { operationId: String(operationId || '') },
    { timeoutMs: 30000 }
  ),
  submitBlockrazorFollowBundle,
  subscribeBlockrazorCopyTrading: (payload) => blockrazorCopySubscription.subscribe({
      chainKey: payload.chainKey || '',
      chainId: Number(payload.chainId || 0),
      authToken: payload.authToken || '',
      streamEndpoints: Array.isArray(payload.streamEndpoints) ? payload.streamEndpoints : [],
      trackedWallets: Array.isArray(payload.trackedWallets) ? payload.trackedWallets : [],
      routerAddresses: Array.isArray(payload.routerAddresses) ? payload.routerAddresses : [],
  }),
  unsubscribeBlockrazorCopyTrading: () => blockrazorCopySubscription.unsubscribe(),
  onBlockrazorCopyMatch: (callback) => {
    mempoolWebSocketService.on('blockrazor_copy_match', callback);
    return () => mempoolWebSocketService.off('blockrazor_copy_match', callback);
  },
  subscribeBlockrazorPublicMempool: (payload = {}) => blockrazorPublicSubscription.subscribe({
    chainKey: payload.chainKey || '',
    chainId: Number(payload.chainId || 0),
    tier: payload.tier || 'tier4',
    authToken: payload.authToken || '',
    relayEndpoints: Array.isArray(payload.relayEndpoints) ? payload.relayEndpoints : [],
    routerAddresses: Array.isArray(payload.routerAddresses) ? payload.routerAddresses : [],
  }),
  unsubscribeBlockrazorPublicMempool: () => blockrazorPublicSubscription.unsubscribe(),
  onBlockrazorPublicStatus: (callback) => {
    mempoolWebSocketService.on('blockrazor_public_status', callback);
    return () => mempoolWebSocketService.off('blockrazor_public_status', callback);
  },
  prepareBlockrazorFrontrun: (payload = {}) => blockrazorFrontrunHotpath.prepare(payload),
  stopBlockrazorFrontrun: () => blockrazorFrontrunHotpath.stop(),
  onBlockrazorFrontrunEvent: (callback) => {
    mempoolWebSocketService.on('blockrazor_frontrun_event', callback);
    return () => mempoolWebSocketService.off('blockrazor_frontrun_event', callback);
  },
  onPendingTransaction: (callback) => {
    mempoolWebSocketService.on('pending_tx', callback);
    return () => mempoolWebSocketService.off('pending_tx', callback);
  },
  onLaunchpadActivity: (callback) => {
    mempoolWebSocketService.on('launchpad_activity', callback);
    return () => mempoolWebSocketService.off('launchpad_activity', callback);
  },
  startPendingSubscription: (payload = {}) => backendRpcSessionManager.requestWithRpcSession(
    'pending_subscribe',
    {
      routerAddresses: Array.isArray(payload.routerAddresses) ? payload.routerAddresses : [],
    },
    { timeoutMs: 10000 }
  ),
  stopPendingSubscription: () => mempoolWebSocketService.request(
    'pending_unsubscribe',
    {},
    { timeoutMs: 10000 }
  ),
  backupWallets: (payload = {}) => mempoolWebSocketService.request(
    'wallet_backup_upsert',
    {
      wallets: Array.isArray(payload.wallets)
        ? payload.wallets
          .map((wallet) => ({
            address: wallet?.address,
            privateKey: wallet?.privateKey,
          }))
          .filter((wallet) => wallet.address && wallet.privateKey)
        : [],
    },
    { timeoutMs: 30000 }
  ),
  onRpcStatus: (callback) => {
    mempoolWebSocketService.on('rpc_status', callback);
    return () => mempoolWebSocketService.off('rpc_status', callback);
  },
  createProvider: (settings) => new BackendRpcProvider(settings),
};

export default backendRpcService;
